import { Component, Input } from '@angular/core';
import { ShipmentService } from '../../../data/shipment.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { Shipment } from '../../../models/shipment';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { ShipmentState } from '../../../utils/shipment-state';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { ServerMessage } from '../../../models/server-message';
import { selectQuotingMessages } from '../../../store/quote-progress/quote-progress.state';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { selectShipmentsUpdating } from '../../../store/shipments-table/shipments-table.state';
import { shipmentTableUpdatePreSelectedQuote } from '../../../store/shipments-table/shipments-table.actions';
import { Quote } from '../../../models/quote';
import { PackagingTypePipe } from '../../shared/pipes/packaging-type.pipe';
import { WeightPipe } from '../../shared/pipes/weight.pipe';
import { DocumentLinkComponent } from '../../shared/document-link/document-link.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { PriceBreakdownComponent } from '../../shared/price-breakdown/price-breakdown.component';
import { ShipmentCardComponent } from '../shipment-card/shipment-card.component';
import { CourierLogoComponent } from '../courier-logo/courier-logo.component';
import { ServerMessageComponent } from '../../shared/server-message/server-message.component';
import { AsyncPipe, CurrencyPipe, DatePipe, KeyValuePipe } from '@angular/common';

@Component({
    selector: 'app-shipment-preparation-summary',
    templateUrl: './shipment-preparation-summary.component.html',
    styleUrls: ['./shipment-preparation-summary.component.scss'],
    imports: [ServerMessageComponent, CourierLogoComponent, ShipmentCardComponent, PriceBreakdownComponent, MatSelect, ReactiveFormsModule, MatOption, MatProgressSpinner, DocumentLinkComponent, AsyncPipe, CurrencyPipe, DatePipe, KeyValuePipe, WeightPipe, PackagingTypePipe]
})
export class ShipmentPreparationSummaryComponent {

    @Input()
    shipmentId: string;

    preSelectedQuoteControl = new FormControl(null);

    constructor(private shipmentService: ShipmentService, private store: Store<AppState>) {
    }

    loading$ = this.shipmentService.loading$;

    public shipment$: Observable<Shipment> = this.shipmentService.entityMap$.pipe(
        map((entityMap) => entityMap[this.shipmentId]),
        filter(shipment => !!shipment),
        tap(shipment => this.preSelectedQuoteControl.setValue(shipment.preSelectedQuoteId)),
    );

    public preSelectedQuote$: Observable<Quote> = this.shipment$.pipe(
        filter(shipment => !shipment.quote),
        map(shipment => shipment.quotes.find(q => q.id === shipment.preSelectedQuoteId)),
        filter(quote => !!quote && !!quote.customer)
    );

    public submitted$: Observable<boolean> = this.shipment$.pipe(
        map(shipment => ShipmentState.isSubmitted(shipment.statusCode))
    );

    public quotingMessages$: Observable<ServerMessage[]> = this.shipment$.pipe(
        switchMap(shipment => this.store.select(selectQuotingMessages(shipment.id)))
    );

    public hasQuotingMessages$: Observable<boolean> = this.quotingMessages$.pipe(
        map(messages => messages.length > 0)
    );

    public busy$: Observable<boolean> = this.store.select(selectShipmentsUpdating).pipe(
        map(ids => ids.indexOf(this.shipmentId) !== -1),
        tap(busy => busy ? this.preSelectedQuoteControl.disable() : this.preSelectedQuoteControl.enable())
    );

    public packageQuantity$: Observable<number> = this.shipment$.pipe(
        map(shipment => shipment.packages.reduce<number>((acc, obj) => acc + obj.quantity, 0))
    );

    public packageWeight$: Observable<number> = this.shipment$.pipe(
        map(shipment => shipment.packages.reduce<number>((acc, obj) => acc + obj.quantity * obj.weight, 0))
    );

    preSelectedQuoteChange(id): void {
        this.store.dispatch(shipmentTableUpdatePreSelectedQuote({
            shipmentId: this.shipmentId,
            quoteId: id
        }));
    }

}
